import {
  faCamera,
  faCaretDown,
  faCaretUp,
  faFire,
  faLock,
  faMessage,
  faRocket,
  faUserCheck,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { FormattedMessage, FormattedRelativeTime } from "react-intl";
import { useNavigate } from "react-router";
import { fetchMembersFromIds } from "../../Api/member";
import {
  chatChannelsAdd,
  chatChannelsInformations,
  membersInformations_members,
} from "../../Api/types";
import { api_postValueOrErrorMessage } from "../../Api/util";
import Angel from "../../Component/Filters/svg/angel.svg?react";
import Cutie from "../../Component/Filters/svg/cutie.svg?react";
import { OPEN_CHANNELS } from "../../Hook/openChannels";
import { displayPopin } from "../../Hook/popins";
import useUser, { USER } from "../../Hook/user";
import Avatar from "../Avatar";
import Pseudonym from "../Member/Pseudonym";

/**
 * The member card set from turn 34: one band card for every list that shows people,
 * one line for the chat list, and the podium pieces for the top. Everything on them
 * is served by membersInformations — the camera count is nb_public_ephemes, the
 * badges are is_angel / is_cutie, the buttons follow can_chat and can_follow.
 */

/** The contract drops the distance past 100 km — a city is enough at that range. */
const DISTANCE_CEILING_KM = 100;

/** Green dot when online — and only then. Last seen is the chat list's column. */
const OnlineDot = ({ member }: { member: membersInformations_members }) =>
  member.is_online ? <span className="w-[7px] h-[7px] flex-none rounded-full bg-online" /> : null;

/** age · city · distance, each part only when served. */
function PlaceLine({ member }: { member: membersInformations_members }) {
  const parts: React.ReactNode[] = [];
  if (member.age_range)
    parts.push(<FormattedMessage key="age" id={"age_range__value_label__" + member.age_range} />);
  if (member.cityInformations) parts.push(member.cityInformations.name);
  if (member.distance && member.distance <= DISTANCE_CEILING_KM)
    parts.push(`${member.distance} km`);
  if (parts.length === 0) return null;
  return (
    <div className="text-explain text-muted whitespace-nowrap overflow-hidden text-ellipsis">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="opacity-40"> · </span>}
          {part}
        </React.Fragment>
      ))}
    </div>
  );
}

/** Flag and status badges as one group. White-filled marks ride a scrim so they hold in both modes. */
function FlagBadges({ member, scrim }: { member: membersInformations_members; scrim?: boolean }) {
  const iso = member.countryInformations?.iso;
  if (!iso && !member.is_angel && !member.is_cutie) return null;
  const marks = (
    <>
      {iso && (
        <img
          className="w-[15px] h-[11px] flex-none rounded-[2px] object-cover"
          src={`https://flagcdn.com/32x24/${iso}.png`}
          alt={iso}
        />
      )}
      {member.is_angel && <Angel className={"h-3.5 w-auto flex-none fill-current [&_*]:fill-current " + (scrim ? "text-white" : "text-muted")} />}
      {member.is_cutie && <Cutie className={"h-3.5 w-auto flex-none fill-current [&_*]:fill-current " + (scrim ? "text-white" : "text-muted")} />}
    </>
  );
  if (!scrim) return marks;
  return (
    <div className="absolute z-10 right-2 top-2 flex items-center gap-1.5 px-[7px] py-[3px] rounded-pill bg-tint-scrim">
      {marks}
    </div>
  );
}

/** The camera count on the image — public snaps, from nb_public_ephemes. */
const CameraPill = ({ member, position }: { member: membersInformations_members; position: string }) => (
  <div
    className={
      "absolute z-10 flex items-center gap-1 px-[7px] py-[3px] rounded-pill bg-tint-scrim text-micro font-medium text-white " +
      position
    }
  >
    <FontAwesomeIcon className="text-[9px]" icon={faCamera} />
    {member.nb_public_ephemes ?? 0}
  </div>
);

/** The one shared chat-open flow — the same steps the profile's Message button takes. */
export function openChatWith(member: membersInformations_members) {
  if (USER?.informations?.type === "guest") {
    return displayPopin()("login", { errorMessage: "terminated_guest_freetour" });
  }
  const err = USER?.informations?.restrictions?.find((e) => e.type === "chat_access");
  if (err) return displayPopin()("chaterror", { error: err });

  api_postValueOrErrorMessage<chatChannelsAdd>("chat/channels/add", {
    members: [member.id],
  }).then((channel) => {
    if (channel.error_message) return;
    api_postValueOrErrorMessage<chatChannelsInformations>("chat/channels/informations", {
      channels: [channel.channel],
    }).then(async (chan) => {
      if (chan.error_message) return;
      const currentChan = chan.channels[0];
      const otherId = currentChan.members.find((e) => e !== USER?.informations?.id);
      currentChan.otherMember = (await fetchMembersFromIds([otherId as string]))[0];
      OPEN_CHANNELS.openChannel(currentChan);
    });
  });
}

/**
 * Follow as a compact icon button. Same state and endpoints as FollowButton — the
 * followed set on the user hook, synced across cards through the "followed" event.
 * The followed state flips to the member's gender colour, the same meaning the
 * pseudonym carries.
 */
function FollowIconButton({ member }: { member: membersInformations_members }) {
  const user = useUser();
  const [followed, setFollowed] = useState(user?.followed.has(member.id));

  useEffect(() => {
    setFollowed(user?.followed.has(member.id));
    const cb = () => setFollowed(user?.followed.has(member.id));
    document.addEventListener("followed", cb);
    return () => document.removeEventListener("followed", cb);
  }, [member]);

  if (member.can_follow === false) return null;
  const gender = `var(--s4s-pseudonym-${["woman", "man", "couple"].includes(member.gender) ? member.gender : "woman"})`;
  return (
    <button
      type="button"
      data-testid="member-follow"
      onClick={(event) => {
        event.stopPropagation();
        if (user?.informations?.type === "guest") {
          return displayPopin()("login", { errorMessage: "terminated_guest_freetour" });
        }
        if (followed) {
          user?.followed.delete(member.id);
          api_postValueOrErrorMessage("members/unfollow", { id: member.id });
        } else {
          user?.followed.add(member.id);
          api_postValueOrErrorMessage("members/follow", { id: member.id });
        }
        setFollowed(!followed);
        document.dispatchEvent(new Event("followed"));
      }}
      className={
        "w-[34px] h-[30px] flex-none flex items-center justify-center rounded-small " +
        (followed ? "" : "text-muted ring-1 ring-inset ring-tint-neutral")
      }
      style={
        followed
          ? { color: gender, background: `color-mix(in srgb, ${gender} 14%, transparent)` }
          : undefined
      }
    >
      <FontAwesomeIcon
        className="text-[13px]"
        icon={followed ? faUserCheck : faUserPlus}
      />
      <span className="sr-only">
        <FormattedMessage id={followed ? "memberitem__unfollow" : "memberitem__follow"} />
      </span>
    </button>
  );
}

/** Where chat is refused the primary degrades instead of vanishing, so the pair keeps its shape. */
function MessageButton({ member }: { member: membersInformations_members }) {
  const canChat = member.can_chat !== false;
  return (
    <button
      type="button"
      disabled={!canChat}
      data-testid="member-message"
      onClick={(event) => {
        event.stopPropagation();
        openChatWith(member);
      }}
      className={
        "flex-1 h-[30px] flex items-center justify-center gap-1.5 rounded-small text-explain font-medium ring-1 ring-inset " +
        (canChat
          ? "text-accent ring-tint-border-accent"
          : "text-muted ring-tint-neutral cursor-default")
      }
    >
      <FontAwesomeIcon className="text-[12px]" icon={canChat ? faMessage : faLock} />
      <FormattedMessage id={canChat ? "memberitem__message" : "memberitem__no_chat"} />
    </button>
  );
}

/** flames_evolution — flames gained today, can be negative. The only progress the API has. */
export function Evolution({ value }: { value?: number }) {
  if (typeof value !== "number") return null;
  const down = value < 0;
  return (
    <span
      className={
        "flex items-center gap-1 text-explain font-medium flex-none " +
        (down ? "text-flame" : "text-online")
      }
    >
      <FontAwesomeIcon className="text-[10px]" icon={down ? faCaretDown : faCaretUp} />
      {Math.abs(value)}
    </span>
  );
}

/**
 * 34b — the chat list line, the one list with a time: it is filtered to members with
 * time_online, so every row is online by definition and the time is the sort key.
 * The freshest row wears the accent with the chat glyph under it. No action buttons:
 * the row already leads into the conversation.
 */
function ChatRow({
  member,
  first,
  onClick,
}: {
  member: membersInformations_members;
  first?: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      data-testid="member-chat-row"
      className="flex items-center gap-3 px-4 py-[11px] cursor-pointer"
    >
      <div className="relative w-[54px] h-[54px] flex-none rounded-control overflow-hidden bg-well">
        <Avatar member={member} />
        <CameraPill member={member} position="left-1 bottom-1" />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
        <div className="flex items-center gap-[7px] min-w-0">
          <OnlineDot member={member} />
          <Pseudonym member={member} />
          <FlagBadges member={member} />
        </div>
        <PlaceLine member={member} />
      </div>
      {member.time_online !== undefined && (
        <div className="flex flex-col items-end gap-[5px] flex-none">
          <span className={"text-explain " + (first ? "font-medium text-accent" : "text-muted")}>
            <FormattedRelativeTime
              value={member.time_online - Date.now() / 1000}
              numeric="auto"
              updateIntervalInSeconds={60}
            />
          </span>
          {first && <FontAwesomeIcon className="text-[15px] text-accent" icon={faMessage} />}
        </div>
      )}
    </div>
  );
}

/** The rank pill: gold for the first place, quiet for everyone else. */
const rankPillClass = (rank: number) =>
  rank === 1 ? "bg-tint-rank text-gold" : "bg-tint-neutral text-muted";

/**
 * 34c — one step of the podium. First place carries the gold: a tinted gradient on
 * the card and a ring on the portrait, both mixed from the semantic gold so a brand
 * or mode swap leaves the metal alone.
 */
export function PodiumCard({
  member,
  rank,
  progress,
  className = "",
}: {
  member: membersInformations_members;
  rank: number;
  /** Flames today is a movement board; all time is a standing — rank only. */
  progress?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const first = rank === 1;
  return (
    <div
      onClick={() => navigate("/member/" + member.id)}
      data-testid={"top-podium-" + rank}
      className={
        "flex flex-col items-center gap-[7px] rounded-control bg-card shadow-card cursor-pointer overflow-hidden " +
        (first ? "px-2 pt-3.5 pb-3 " : "px-2 pt-3 pb-[11px] ") +
        className
      }
      style={
        first
          ? {
              background:
                "linear-gradient(165deg, color-mix(in srgb, var(--s4s-gold) 18%, var(--s4s-card)), var(--s4s-card) 74%)",
            }
          : undefined
      }
    >
      <div
        className={
          "relative flex-none rounded-small overflow-hidden bg-well " +
          (first ? "w-[68px] h-[68px] desktop:w-20 desktop:h-20" : "w-14 h-14 desktop:w-[66px] desktop:h-[66px]")
        }
        style={
          first
            ? { boxShadow: "0 0 0 2px color-mix(in srgb, var(--s4s-gold) 55%, transparent)" }
            : undefined
        }
      >
        <Avatar member={member} />
      </div>
      <div className="flex items-center gap-1.5 max-w-full min-w-0">
        <OnlineDot member={member} />
        <Pseudonym member={member} />
      </div>
      {progress ? <Evolution value={member.flames_evolution} /> : <PlaceLine member={member} />}
      <div
        className={
          "flex-none rounded-pill flex items-center justify-center font-semibold " +
          (first ? "w-6 h-6 text-meta " : "w-[22px] h-[22px] text-explain ") +
          rankPillClass(rank)
        }
      >
        {rank}
      </div>
    </div>
  );
}

/**
 * 34c — the list below the podium. The signed-in member's own row is lifted on an
 * accent tint, so the answer to "where am I?" finds the eye by itself.
 */
export function RankRow({
  member,
  rank,
  progress,
}: {
  member: membersInformations_members;
  rank: number;
  progress?: boolean;
}) {
  const navigate = useNavigate();
  const self = member.id === USER?.informations?.id;
  return (
    <div
      onClick={() => navigate("/member/" + member.id)}
      data-testid={"top-rank-" + rank}
      className={
        "flex items-center gap-3 px-4 py-[11px] cursor-pointer" +
        (self ? " bg-[color-mix(in_srgb,var(--s4s-accent)_10%,transparent)]" : "")
      }
    >
      <div
        className={
          "w-6 flex-none text-right text-row font-medium " + (self ? "text-accent" : "text-muted")
        }
      >
        {rank}
      </div>
      <div
        className={
          "relative w-[46px] h-[46px] flex-none rounded-small overflow-hidden bg-well" +
          (self ? " ring-2 ring-tint-border-accent" : "")
        }
      >
        <Avatar member={member} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
        <div className="flex items-center gap-[7px] min-w-0">
          <OnlineDot member={member} />
          <Pseudonym member={member} />
          <FlagBadges member={member} />
        </div>
        <PlaceLine member={member} />
      </div>
      {progress && <Evolution value={member.flames_evolution} />}
    </div>
  );
}

/**
 * 34a — the band, the default member item: square portrait, meta and both actions
 * off the photo on the card surface. One item for every list except chat and top.
 * The whole card opens the profile; the two buttons stop the tap short of it.
 */
function BandCard({
  member,
  common,
  blocked,
  onClick,
}: {
  member: membersInformations_members;
  common?: boolean;
  blocked?: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      data-testid="member-card"
      className="w-full col-span-1 rounded-control overflow-hidden cursor-pointer bg-card shadow-card"
    >
      <div className="relative aspect-square bg-well overflow-hidden">
        <Avatar member={member} />
        <CameraPill member={member} position="left-2 top-2" />
        <FlagBadges member={member} scrim />
      </div>
      <div className="px-2.5 pt-2 pb-2.5 flex flex-col gap-[7px]">
        <div className="flex items-center gap-1.5 min-w-0">
          <OnlineDot member={member} />
          <Pseudonym cards member={member} />
        </div>
        <PlaceLine member={member} />
        {common && (
          <div className="flex items-center gap-[9px] text-explain">
            <span className="flex items-center gap-1 text-flame">
              <FontAwesomeIcon icon={faFire} />
              {member.common_flames ?? 0}
            </span>
            {typeof member.common_boost === "number" && (
              <span className="flex items-center gap-1 text-accent">
                <FontAwesomeIcon icon={faRocket} />
                +{member.common_boost}%
              </span>
            )}
          </div>
        )}
        {/* The blocked list's card is a door to the unblock popin — no actions. */}
        {!blocked && (
          <div className="flex gap-1.5">
            <MessageButton member={member} />
            <FollowIconButton member={member} />
          </div>
        )}
      </div>
    </div>
  );
}

function MemberItem({
  member,
  chat,
  common,
  first,
  onClick,
  blocked,
  reload,
}: {
  chat?: boolean;
  reload?: any;
  blocked?: boolean;
  common?: boolean;
  /** The chat list's freshest row — it wears the accent. */
  first?: boolean;
  onClick?: any;
  member: membersInformations_members;
}) {
  const navigate = useNavigate();
  const open = () => {
    if (blocked) return displayPopin()("blocked", { member, reload });
    if (onClick) onClick();
    else navigate("/member/" + member.id);
  };
  if (chat) return <ChatRow member={member} first={first} onClick={open} />;
  return <BandCard member={member} common={common} blocked={blocked} onClick={open} />;
}

export default MemberItem;
